(ns ycweb.app.admin.wholesale.importorders.common
  (:require
   [clojure.set :as set]
   [util.transform :as trf]
   [ycweb.util.core :as yuc]
   [ycweb.util.date :as yud]))

(def api-permissions
  #{"SiteAdmin:Wholesale:ImportOrder:View"
    "SiteAdmin:Teams:Finance"
    "SiteAdmin:Teams:Development"})

(def import-order-edit-permission
  #{"SiteAdmin:Wholesale:ImportOrder:Edit"
    "SiteAdmin:Teams:Development"})

(def import-order-manager-permission-set
  #{"SiteAdmin:Wholesale:Manager:ImportOrder"
    "SiteAdmin:Teams:Development"})

(def csv-headers
  ["Create Date" "Latest Ship Date" "Supplier" "Supplier Name" "Currency Code" "Order No"
   "Origin Country" "Supplier Country" "Import Country" "Transport Mode" "Discharge Port"
   "Lading Port" "Freight Terms" "Division Code" "Location" "Location Name" "FirstPaymentDate"
   "First Payment Amount" "Second Payment Date" "Second Payment Amount" "Third Payment Date"
   "Third Payment Amount"])

(def csv-item-headers
  ["Brand" "SKU" "Product Description" "Size CBM" "Unit Size" "Quantity" "Unit Amount" "Amount" "HTS"])

(defn transform-import-order [import-order {:keys [open?]}]
  (->
    import-order
    (update :first_payment_amount trf/fromcurrency)
    (update :second_payment_amount trf/fromcurrency)
    (update :third_payment_amount trf/fromcurrency)
    (assoc
      :open? (or open? (= (:status import-order) "Draft"))
      :disabled? (not (contains? #{"Draft" "Ordered"} (:status import-order))))
    (set/rename-keys
      {:supplier_id :supplier-id
       :supplier_country_id :supplier-country-id
       :origin_country_id :origin-country-id
       :import_country_id :import-country-id
       :discharge_port_locode_id :discharge-port-locode-id
       :lading_port_locode_id :lading-port-locode-id
       :currency_code :currency-code
       :incoterm_id :incoterm-id
       :freight_forwarder_id :freight-forwarder-id
       :transport_mode :transport-mode
       :order_date :order-date
       :ship_by_date :ship-by-date
       :destination_id :destination-id
       :first_payment_date :first-payment-date
       :first_payment_amount :first-payment-amount
       :second_payment_date :second-payment-date
       :second_payment_amount :second-payment-amount
       :third_payment_date :third-payment-date
       :third_payment_amount :third-payment-amount
       :expected_landing_date :expected-landing-date})
    (yuc/map->nsmap "import-order")))

(defn transform-import-order-item [import-order-item {:keys [currency variation origin-country tax-rule]}]
  (->
    (yuc/maybe assoc
      import-order-item
      :product_name (:product/name variation)
      :hs_code (:import-tariff/code variation)
      :product_sku (:name variation)
      :features (:features variation)
      :origin-country/name (:name origin-country)
      :tax-rule/description (:description tax-rule)
      :currency/symbol-unicode (:unicode currency))
    (set/rename-keys
      {:import_order_id :import-order-id
       :variation_id :variation-id
       :features :variation/features
       :tax_rule_id :tax-rule-id
       :origin_country_id :origin-country-id
       :product_name :product-name
       :product_sku :product-sku
       :hs_code :hs-code
       :unit_cost :unit-cost
       :unit_cbm :unit-cbm})
    (yuc/map->nsmap "import-order-item")))

(defn transform-locode [locode]
  (->
    locode
    (yuc/map->nsmap "locode")))

(defn validate-ship-by-date [{:keys [ihdb]} row value]
  (let [status (:import-order/status row)
        order-date (:import-order/order-date row)]

    (cond
      (and
        (not= "Draft" status)
        (nil? value))
      "The ship by date must be set."

      (and order-date
        (< (yud/seconds value) (yud/seconds order-date)))
      "The ship by date must be after the order date.")))

(defn validate-expected-landing-date [{:keys [ihdb]} row value]
  (let [order-date (:import-order/order-date row)
        ship-by-date (:import-order/ship-by-date row)]
    (cond
      (and order-date
        value
        (< (yud/seconds value) (yud/seconds order-date)))
      "The expected landing date must be after the order date."

      (and ship-by-date
        value
        (< (yud/seconds value) (yud/seconds ship-by-date)))
      "The expected landing date must be after the ship by date.")))

(defn import-order-validation-rules [action opts]
  (let [required-unless
        {:rule :required-unless
         :config {:field :import-order/status :value "Draft"}}]
    (yuc/maybe assoc
      {:import-order/freight-forwarder-id
       [:nillable required-unless
        {:rule :in
         :config {:list (:freight-forwarder-ids opts [])}}]
       :import-order/status
       [:required
        {:rule :in
         :config {:list (:statuses opts [])}}]
       :import-order/transport-mode
       [:nillable required-unless
        {:rule :in
         :config {:list (:transport-modes opts [])}}]
       :import-order/supplier-country-id
       [:nillable required-unless
        {:rule :in
         :config {:list (:country-ids opts [])}}]
       :import-order/origin-country-id
       [:nillable required-unless
        {:rule :in
         :config {:list (:shipping-country-ids opts [])}}]
       :import-order/import-country-id
       [:nillable required-unless
        {:rule :in
         :config {:list (:shipping-country-ids opts [])}}]
       :import-order/discharge-port-locode-id
       [:nillable required-unless
        {:rule :in-group
         :config
         {:list (:locode-ids opts [])
          :group-key :import-order/import-country-id}}]
       :import-order/lading-port-locode-id
       [:nillable required-unless
        {:rule :in-group
         :config
         {:list (:locode-ids opts [])
          :group-key :import-order/origin-country-id}}]
       :import-order/incoterm-id
       [:nillable required-unless
        {:rule :in
         :config {:list (:incoterm-ids opts [])}}]
       :import-order/order-date
       [:nillable
        {:rule :required-if
         :config {:field :import-order/status :value ["Confirmed" "On Water" "Shipping Booked" "Arrived"]}}
        {:rule :date}]
       :import-order/ship-by-date
       [:nillable
        {:rule :date}
        {:rule :fn
         :rule-fn (partial validate-ship-by-date opts)}]
       :import-order/expected-landing-date
       [:nillable
        {:rule :date}
        {:rule :fn
         :rule-fn (partial validate-expected-landing-date opts)}]
       :import-order/first-payment-date
       [:nillable {:rule :date} required-unless
        {:rule :required-with
         :config {:fields [:import-order/second-payment-date]}}]
       :import-order/first-payment-amount
       [:nillable :integer
        {:rule :required-with
         :config {:fields [:import-order/second-payment-amount]}}]
       :import-order/second-payment-date
       [:nillable {:rule :date}
        {:rule :required-with
         :config {:fields [:import-order/third-payment-date]}}]
       :import-order/second-payment-amount
       [:nillable :integer
        {:rule :required-with
         :config {:fields [:import-order/third-payment-amount]}}]
       :import-order/third-payment-date [:nillable {:rule :date}]
       :import-order/third-payment-amount [:nillable :integer]})))